import React, { useEffect, useState } from "react";
import { Card, Col, ListGroup, Row, Stack, Tab, Table, Spinner } from "react-bootstrap";
import { SingleCardMetric } from "./ui/SingleCardMetric";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong, faArrowRightLong, faDownload, faRefresh } from "@fortawesome/free-solid-svg-icons";
import ContinuumCategoryPicker from "../report-ui/ContinuumCategoryPicker";
import { fetchReportContinuum } from "../../services/httpClient";
import { CSVLink } from "react-csv";
import IconEmbeddedButton from "../ui/IconEmbeddedButton";
import { getCurrentDateTime } from "../../helpers/date";

const AssessmentContinuum = ({ setCsvHeaders, setCsvData }: any) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [contData, setContData] = useState<any[]>();
  const [inCrisis, setInCrisis] = useState<any>();
  const [atRisk, setAtRisk] = useState<any>();
  const [contTableData, setContTableData] = useState<any>();
  const [filteredTableData, setFilteredTableData] = useState<any>();
  const [crisisFilter, setCrisisFilter] = useState<boolean>(false);
  const [riskFilter, setRiskFilter] = useState<boolean>(false);

  const contHeaders = [
    { label: "ID", key: "id" },
    { label: "Assessment Name", key: "name" },
    { label: "Case ID", key: "case_id" },
    { label: "Case Name", key: "case_name" },
    { label: "Completed", key: "assessment_at" },
    { label: "Category", key: "key" },
    { label: "Status", key: "value" },
  ];

  const csvHeaders = [
    { label: "Assessment Date", key: "date" },
    { label: "Case Name", key: "case_name" },
    { label: "Case ID", key: "case_id" },
    { label: "Assessment ID", key: "assessment_id" },
    { label: "Assessment Name", key: "assessment_name" },

    { label: "Composite", key: "composite" },

    { label: "Income", key: "income" },
    { label: "Income Description", key: "income_val" },

    { label: "Education", key: "education" },
    { label: "Education Description", key: "education_val" },

    { label: "Employment", key: "employment" },
    { label: "Employment Description", key: "employment_val" },

    { label: "Childcare", key: "childcare" },
    { label: "Childcare Description", key: "childcare_val" },

    { label: "Health Care", key: "healthcare" },
    { label: "Health Description", key: "healthcare_val" },

    { label: "Nutrition", key: "nutrition" },
    { label: "Nutrition Description", key: "nutrition_val" },

    { label: "Housing", key: "housing" },
    { label: "Housing Description", key: "housing_val" },

    { label: "Transportation", key: "transportation" },
    { label: "Transportation Description", key: "transportation_val" },

    { label: "Support Systems", key: "support" },
    { label: "Support Systems Description", key: "support_val" },

    { label: "Family Interactions", key: "family" },
    { label: "Family Interactions Description", key: "family_val" },
  ];

  useEffect(() => {
    getContinuumData();
    setCsvHeaders(csvHeaders);
  }, []);

  const getContinuumData = () => {
    fetchReportContinuum()
      .then((res: any) => {
        const cont = res[0].data;
        setContData(cont);

        cleanFullTableData(cont);
        const crisis = cont?.filter((c: any) => c.value === "In-Crisis");
        setInCrisis(crisis);
        const risk = cont?.filter((c: any) => c.value === "At-Risk");
        setAtRisk(risk);
        const tdata = cont?.filter((c: any) => c.value === "At-Risk" || c.value === "In-Crisis");
        setContTableData(tdata);
        setFilteredTableData(tdata);
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const cleanFullTableData = (data: any) => {
    let tdata: any[] = [];
    // Group data by id
    const grouped: { [id: string]: any[] } = {};
    data.forEach((item: any) => {
      if (!grouped[item.id]) grouped[item.id] = [];
      grouped[item.id].push(item);
    });

    Object.values(grouped).forEach((group: any[]) => {
      // Each group should represent a single assessment instance
      const row: any = {};
      const first = group[0] || {};
      row.date = first.assessment_at || "";
      row.case_name = first.case_name || "";
      row.case_id = first.case_id || "";
      row.assessment_id = first.id || "";
      row.assessment_name = first.name || "";

      const keys = [
        "childcare",
        "employment",
        "education",
        "income",
        "family",
        "support",
        "transportation",
        "housing",
        "nutrition",
        "healthcare",
      ];
      let sum = 0;
      keys.forEach((key) => {
        const item = group.find((d: any) => d.key === key);
        row[key] = setToNums(item?.value) || "";
        row[key + "_val"] = item?.value || "";
        sum += parseInt(row[key]) || 0;
      });
      row.composite = Math.round((sum / 10) * 10) / 10;
      tdata.push(row);
    });
    setCsvData(tdata);
  };

  const setToNums = (data: any) => {
    if (data === "In-Crisis") return "1";
    if (data === "At-Risk") return "2";
    if (data === "Safe") return "3";
    if (data === "Stable") return "4";
    if (data === "Thriving") return "5";
    return false;
  };

  const handleInCrisisClick = () => {
    const filtered = contTableData?.filter((c: any) => c.value === "In-Crisis");
    setFilteredTableData(filtered);
    setCrisisFilter(true);
    setRiskFilter(false);
  };

  const handleAtRiskClick = () => {
    const filtered = contTableData?.filter((c: any) => c.value === "At-Risk");
    setFilteredTableData(filtered);
    setCrisisFilter(false);
    setRiskFilter(true);
  };

  const handleRefreshClick = () => {
    setFilteredTableData(contTableData);
    setCrisisFilter(false);
    setRiskFilter(false);
  };

  return (
    <div>
      {isLoading ? (
        <div className="text-center mt-5">
          <Spinner animation="border" style={{ color: "#F6893D" }} />
        </div>
      ) : (
        <>
          <Card className="bg-light mt-3 mb-3">
            <Card.Body>
              <Row className="gx-0">
                <Tab.Container>
                  <Row className="gx-0">
                    <Col className="text-start">
                      <h5 className="app-header-text">
                        <FontAwesomeIcon icon={faArrowLeftLong} className="ms-3" /> Dependence
                      </h5>
                    </Col>
                    <Col className="text-end">
                      <h5 className="app-header-text">
                        Independence <FontAwesomeIcon icon={faArrowRightLong} className="me-3" />
                      </h5>
                    </Col>
                  </Row>
                  <Row className="gx-0">
                    <Col className="col-12">
                      <ListGroup horizontal>
                        <ListGroup.Item key="0" className="text-center app-secondary-color m-1" action>
                          In-Crisis
                        </ListGroup.Item>
                        <ListGroup.Item key="1" className="text-center app-secondary-color m-1" action>
                          At-Risk
                        </ListGroup.Item>
                        <ListGroup.Item key="2" className="text-center app-secondary-color m-1" action>
                          Safe
                        </ListGroup.Item>
                        <ListGroup.Item key="3" className="text-center app-secondary-color m-1" action>
                          Stable
                        </ListGroup.Item>
                        <ListGroup.Item key="4" className="text-center app-secondary-color m-1" action>
                          Thriving
                        </ListGroup.Item>
                      </ListGroup>
                    </Col>
                  </Row>
                </Tab.Container>
              </Row>
            </Card.Body>
          </Card>
          <Row id="continuum-pie">
            <Col xs={10}>
              <ContinuumCategoryPicker isAdmin={true} data={contData} />
            </Col>
          </Row>
          {contTableData?.length > 0 && (
            <>
              <Row>
                <Col className="mb-1 col-4">
                  <SingleCardMetric
                    title="In-Crisis"
                    total={inCrisis?.length}
                    onHandleClick={handleInCrisisClick}
                    cursor={true}
                    active={crisisFilter}
                  />
                </Col>
                <Col className="mb-1 col-4">
                  <SingleCardMetric
                    title="At-Risk"
                    total={atRisk?.length}
                    onHandleClick={handleAtRiskClick}
                    cursor={true}
                    active={riskFilter}
                  />
                </Col>
                <Col className="col-1 d-flex flex-column text-end justify-content-end align-items-end">
                  <Stack direction="horizontal" gap={2} className="mb-3">
                    <div id="refresh-continuum-button">
                      <IconEmbeddedButton onClickButton={handleRefreshClick} tooltipText="Refresh" icon={faRefresh} color="primary" />
                    </div>
                    <CSVLink
                      id="download-continuum-button"
                      data={contTableData}
                      headers={contHeaders}
                      filename={`incrisis_atrisk_${getCurrentDateTime()}.csv`}
                      target="_blank"
                    >
                      <IconEmbeddedButton tooltipText="Data Export" icon={faDownload} />
                    </CSVLink>
                  </Stack>
                </Col>
              </Row>
              {filteredTableData?.length > 0 ? (
                <Row className="mb-3">
                  <Col xs={12} lg={8}>
                    <Table striped>
                      <thead>
                        <tr>
                          <th>Case Name</th>
                          <th>Category</th>
                          <th>Status</th>
                          <th>Completed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTableData?.map((d: any) => (
                          <tr key={d.field_value_id}>
                            <td>{d.case_name}</td>
                            <td className="first-letter">{d.key}</td>
                            <td>{d.value}</td>
                            <td>{d.assessment_at}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Col>
                </Row>
              ) : (
                <p className="mt-2">No Continuum Records</p>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default AssessmentContinuum;
